import mixpanel from "mixpanel-browser";
import { injectable } from "inversify";
import { computed, makeObservable, observable } from "mobx";
import {
  TrackLoginParams,
  TrackUsersImportedParams,
  TrackUserInvitedParams,
  TrackUserPermissionUpdatedParams,
  TrackCsvExportedParams,
  TrackDashboardCsvExportedParams,
  TrackDashboardViewedParams,
  IMixpanelService,
  IMixpanelEventBuilder,
  MixpanelInitParams,
  TrackEventParams,
} from "./types";
import { getPermissionChanges } from "../platformPermissions";
import { MixpanelEventBuilder } from "./MixpanelEventBuilder";
import { env } from "./env";

const MIXPANEL_EVENT_NAMES = {
  USER_LOGGED_IN: "User Logged In",
  USER_LOGGED_OUT: "User Logged Out",
  USERS_IMPORTED: "Users Imported",
  USER_INVITED: "User Invited",
  USER_PERMISSION_UPDATED: "User Permission Updated",
  CSV_EXPORTED: "CSV Exported",
  DASHBOARD_CSV_EXPORTED: "Dashboard CSV Exported",
  DASHBOARD_VIEWED: "Dashboard Viewed",
} as const;

@injectable()
export class MixpanelService implements IMixpanelService {
  private isInitialized = false;
  private source?: string;
  private orgId?: string;

  constructor() {
    makeObservable<MixpanelService, "isInitialized" | "shouldTrack" | "source" | "orgId">(this, {
      isInitialized: observable,
      source: observable,
      orgId: observable,
      shouldTrack: computed,
    });
  }

  init({ token, user, logger, source, orgId }: MixpanelInitParams) {
    if (this.isInitialized || !token) {
      logger?.warn({ missingToken: !token, isInitialized: this.isInitialized }, "failed to initialize Mixpanel");
      return;
    }

    this.source = source;
    this.orgId = orgId;

    mixpanel.init(token, {
      track_pageview: "url-with-path",
      persistence: "localStorage",
      ignore_dnt: true,
      debug: env.MODE === "development" && env.VITE_MIXPANEL_DEBUG === "true",
      secure_cookie: env.MODE === "production" || env.MODE === "staging",
      loaded: () => {
        this.isInitialized = true;

        if (user) {
          const currentId = mixpanel.get_distinct_id();
          if (currentId !== user._id) {
            this.identifyUser(user._id, {
              email: user.email,
              username: user.username,
              orgId: user.organizationId || this.orgId,
              language: user.language,
            });
          }
        }
      },
    });
  }

  /**
   * Creates a new event builder instance
   */
  createEventBuilder(): IMixpanelEventBuilder {
    return new MixpanelEventBuilder(this.executeTrackEvent.bind(this), () => this.shouldTrack, this.orgId);
  }

  trackLogin({ userId, authSource, email, username, language }: TrackLoginParams) {
    if (!this.shouldTrack) return;

    mixpanel.identify(userId);
    mixpanel.people.set(
      {
        $last_login: new Date().toISOString(),
        $email: email,
        OrgID: this.orgId,
        Username: username,
        Language: language,
      },
      () => {
        this.createEventBuilder().setEventName(MIXPANEL_EVENT_NAMES.USER_LOGGED_IN).addProperty("Authentication Method", authSource).track();
      }
    );
  }

  trackLogout() {
    this.createEventBuilder()
      .setEventName(MIXPANEL_EVENT_NAMES.USER_LOGGED_OUT)
      .setCallback(() => {
        mixpanel.reset();
      })
      .track();
  }

  trackUsersImported({ userCount, remoteSync, success, ignoredCount }: TrackUsersImportedParams) {
    this.createEventBuilder()
      .setEventName(MIXPANEL_EVENT_NAMES.USERS_IMPORTED)
      .addProperties({
        UserCount: userCount,
        RemoteSync: remoteSync,
        Success: success,
        IgnoredCount: ignoredCount,
      })
      .track();
  }

  trackUserInvited({ success, inviteToPlatform, inviteToApplications }: TrackUserInvitedParams) {
    this.createEventBuilder()
      .setEventName(MIXPANEL_EVENT_NAMES.USER_INVITED)
      .addProperties({
        Success: success,
        "Invite To Platform": inviteToPlatform,
        "Invite To Applications": inviteToApplications,
      })
      .track();
  }

  trackUserPermissionUpdated({ oldPermissions, newPermissions, userId, userName }: TrackUserPermissionUpdatedParams) {
    const permissionChanges = getPermissionChanges(oldPermissions, newPermissions);
    this.createEventBuilder()
      .setEventName(MIXPANEL_EVENT_NAMES.USER_PERMISSION_UPDATED)
      .addProperties({
        userId,
        userName,
        permissionsAdded: permissionChanges.added,
        permissionsRemoved: permissionChanges.removed,
      })
      .track();
  }

  trackCsvExported({ tableName }: TrackCsvExportedParams) {
    this.createEventBuilder().setEventName(MIXPANEL_EVENT_NAMES.CSV_EXPORTED).addProperty("Table Name", tableName).track();
  }

  trackDashboardCsvExported({ dashboardTab, tableName }: TrackDashboardCsvExportedParams) {
    this.createEventBuilder()
      .setEventName(MIXPANEL_EVENT_NAMES.DASHBOARD_CSV_EXPORTED)
      .addProperties({ "Dashboard Tab": dashboardTab, "Table Name": tableName })
      .track();
  }

  // Will be implemented in the next PR
  trackDashboardViewed({ dashboardName }: TrackDashboardViewedParams) {
    this.createEventBuilder().setEventName(MIXPANEL_EVENT_NAMES.DASHBOARD_VIEWED).addProperty("Dashboard Name", dashboardName).track();
  }

  private identifyUser(
    userId: string,
    {
      email,
      username,
      orgId,
      language,
    }: {
      email?: string;
      username?: string;
      orgId?: string;
      language?: string;
    } = {}
  ) {
    if (!this.shouldTrack) return;

    mixpanel.identify(userId);
    this.setUserProperties({
      email,
      username,
      orgId: orgId || this.orgId,
      language,
    });
  }

  private setUserProperties({ email, username, orgId, language }: { email?: string; username?: string; orgId?: string; language?: string }) {
    if (!this.shouldTrack) return;

    const properties: Record<string, any> = {
      OrgID: orgId,
    };

    if (email) {
      properties.$email = email;
    }
    if (username) {
      properties.Username = username;
    }
    if (language) {
      properties.Language = language;
    }

    mixpanel.people.set(properties);
  }

  private executeTrackEvent({ eventName, properties, orgId, callback }: TrackEventParams) {
    const eventProperties = {
      ...properties,
      OrgID: orgId || this.orgId,
      Environment: env.MODE,
      Source: this.source,
    };

    mixpanel.track(eventName, eventProperties, callback);
  }

  private get shouldTrack() {
    if (!this.isInitialized) return false;
    const envMode = env.MODE;
    if (!envMode || envMode === "test") return false;

    return true;
  }
}
